import React from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { IRemoteAgentConnectionStatus } from '~src/settings/dataconnect-settings/serverApi/requests/GetSiteRemoteAgentsConnectionStatusRequest';
import { ISiteAdminDataConnectSettings, ISiteDataConnectSettingsForSiteAdmin } from '~src/settings/types/ISiteAdminDataConnectSettings';
import { Sites } from '~src/siteManagement/services/resources/Sites';
import { incrementRouterSpaceReactKey } from '~src/spacesPlatform/actions/IncrementRouterSpaceReactKeyAction';

import { SettingsFormikContainer } from '../components/common/SettingsFormikContainer';
import { BaasEdgePoolsPayload } from './sections/types/BaasEdgePoolsPayload';

// the only purpose of this type's existence is to map to
// the props(which is the same as the response body for the getDataConnect settings API call)
// that magically get sent to this component
export type SiteDataConnectSettingsForSiteAdminProps = {
  settings: { // this is the same as the type of the response body  in the get DataConnect settings API response
    siteId?: string; // id shouldnt be in the settings passed on to settings
    siteAdminDataConnectSettings?: ISiteAdminDataConnectSettings;
  };
  connectionStatuses?: IRemoteAgentConnectionStatus[];
  baasEdgePools?: BaasEdgePoolsPayload;
};

export type DataConnectSiteSettingsProps = SiteDataConnectSettingsForSiteAdminProps & {
  refreshBaasEdgePools: () => void;
  hasLoadedBaasEdgePools: boolean;
};

const DataConnectSiteSettingsSections = [
  'DataConnectSettingsSummarySection',
  'DataConnectPoolingSection',
  'DataConnectNetworkAllowlist'
];


export function DataConnectSiteSettings(props: DataConnectSiteSettingsProps) {
  const submitHandler = useSubmitHandler(props);

  return <SettingsFormikContainer
    formName={'DataConnectSiteSettings'} // this is required
    onSubmit={submitHandler} // submitHandler gets called when the save button is clicked.
    sections={DataConnectSiteSettingsSections}
    {...props}
  />;
}

function useSubmitHandler(settings: SiteDataConnectSettingsForSiteAdminProps) {
  return React.useCallback(async (newSiteAdminDataConnectSettings: SiteDataConnectSettingsForSiteAdminProps['settings']) => {
    const siteId = newSiteAdminDataConnectSettings.siteId;

    const sitesService = Sites.instance;
    const result: ISiteAdminDataConnectSettings | IResult = await sitesService.updateSiteDataConnectSettingsForSiteAdmin(
      siteId, newSiteAdminDataConnectSettings.siteAdminDataConnectSettings as ISiteAdminDataConnectSettings);

    if (result?.errors?.length) {
      throw result.errors.map((e: any) => sitesService.errorMessage(e.code, null));
    } else {
      /**
        * Have to use a hard reload here to force settings to update with newly saved values
        * TODO: This solution could be improved in the future
        */
      await getSessionManagementServiceInstance().refreshSessionInfo();
      getAppStore().dispatch(incrementRouterSpaceReactKey());

      ToasterService.getInstance().pop({ type: ToastType.Success, text: Messages.settingsUpdateSuccess() });

      // Return a snapshot of saved settings in case API call fails without error
      const savedSettings: ISiteDataConnectSettingsForSiteAdmin = await sitesService.getSiteDataConnectSettingsForSiteAdminById(siteId);
      return {
        settings: savedSettings,
        connectionStatuses: settings.connectionStatuses
      };
    }
  }, [settings]);
}
